import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const AuthPage = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      if (isSignUp) {
        // Validate terms acceptance
        if (!acceptedTerms) {
          setError('You must accept the Terms of Service to create an account');
          setLoading(false);
          return;
        }

        // Sign up
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
            },
          },
        });

        if (authError) throw authError;

        if (authData.user) {
          // Create profile with free tier
          const { error: profileError } = await supabase
            .from('profiles')
            .insert({
              id: authData.user.id,
              email: authData.user.email,
              full_name: fullName,
              subscription_tier: 'free',
              subscription_status: 'active',
              queries_used: 0,
              queries_limit: 10,
              billing_period_start: new Date().toISOString(),
              billing_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            });

          if (profileError) {
            console.error('Profile creation error:', profileError);
            // Don't throw - user is created, profile can be fixed later
          }

          setMessage('Account created successfully! Redirecting...');
          setTimeout(() => {
            window.REACT_APP_NAVIGATE('/oracle');
          }, 1500);
        }
      } else {
        // Sign in
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) throw signInError;

        setMessage('Signed in successfully!');
        setTimeout(() => {
          window.REACT_APP_NAVIGATE('/oracle');
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-cyan-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-2xl p-8 border border-purple-100"
          >
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-purple-600 to-pink-600 rounded-full mx-auto mb-4">
                <i className="ri-user-star-line text-3xl text-white"></i>
              </div>
              <h1 className="font-serif text-3xl font-bold text-gray-900 mb-2">
                {isSignUp ? 'Create Account' : 'Welcome Back'}
              </h1>
              <p className="text-gray-600">
                {isSignUp ? 'Start your journey with 10 free queries' : 'Sign in to continue your journey'}
              </p>
            </div>

            <form onSubmit={handleAuth} className="space-y-5">
              {isSignUp && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your password"
                />
              </div>

              {isSignUp && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      required
                      className="mt-1 w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      I have read and agree to the{' '}
                      <Link 
                        to="/terms" 
                        target="_blank"
                        className="text-purple-600 hover:text-purple-800 font-semibold underline"
                      >
                        Terms of Service
                      </Link>
                      , including the acceptable use policy and security provisions
                    </span>
                  </label>
                </div>
              )}

              {message && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm text-green-800">{message}</p>
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {loading ? 'Processing...' : isSignUp ? 'Create Account' : 'Sign In'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setMessage('');
                  setError('');
                  setAcceptedTerms(false);
                }}
                className="text-sm text-purple-600 hover:text-purple-800 font-semibold cursor-pointer whitespace-nowrap"
              >
                {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
              </button>
            </div>

            {isSignUp && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4 text-center">Free Account Includes:</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-check-line text-lg text-green-600"></i>
                    10 oracle queries per month
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-check-line text-lg text-green-600"></i>
                    Query history tracking
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-check-line text-lg text-green-600"></i>
                    Personal profile
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-check-line text-lg text-green-600"></i>
                    Upgrade anytime
                  </li>
                </ul>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AuthPage;